import { API_VERSION, appSettings } from '../AppSettings';
import { Sample } from './SampleData';
import { BaseGet, BasePost } from './BaseData';
import { uniq } from 'lodash';
import { Transition, TransitionSampleDetailsDto } from './SampleTrackingTransitionData';
import { LabAssignedSampleLabel, SampleTrackingSampleInformation, TransitionSample } from './SampleTrackingData';
import { SupplementalFile } from './SupplementalFileData';
import { InformaticsPipelineResult } from './InformaticsCheckByData';
import { InformaticsComputedValues } from './InformaticsData';

export interface SampleHierarchy {
  root: Sample;
  hierarchy: ReadonlyArray<SampleHierarchyDescendant>;
}

export interface SampleHierarchyDescendant {
  ancestor: Sample;
  descendant: Sample;
  depth: number;
}

export interface SampleHierarchyTree {
  ancestor: Sample;
  descendant: ReadonlyArray<SampleHierarchyTree>;
  depth: number;
  path: string[];
  isLeaf: boolean;
}

export interface SampleTransitionInformation {
  transitionCreatedIn?: Transition;
  transitionSampleCreatedIn?: TransitionSample;
  labAssignedSampleLabelCreatedIn?: LabAssignedSampleLabel;
  currentTransition?: Transition;
  currentTransitionSample?: TransitionSample;
  currentLabAssignedSampleLabel?: LabAssignedSampleLabel;
  sampleId: string;
  sample: Sample;
}

export interface SampleSequencingDetails {
  fastQs?: SequencingFastQ[];
  fastqLocation?: string;
  informaticsInstrumentId?: string;
  informaticsLibraryTypeId?: string;
  libraryKitId?: string;
  libraryKitName?: string;
  sampleId: string;
  sequenceRunId?: string;
  informaticsPipelineRunId?: string;
  sequenceType?: string;
  sequencingLabId?: string;
  sequencingLabName?: string;
  sequencingPlatformId?: string;
  sequencingPlatformName?: string;
}

export interface SampleInformaticsResultDetails {
  sampleId: string;
  details: ReadonlyArray<SampleInformaticsResultDetailsEntry>;
}

export interface InformaticsPipelineRun {
  informaticsPipelineRunId: string;
  callbackId: string;
  sampleId: string;
  informaticsOrderId: string;
  sequenceId?: string;
  sequenceRunId?: string;
  tissueTypeId: string;
  sequencingLabId: string;
  sequencingPlatformId: string;
  completedAt?: Date;
  errorMessage?: string;
  createdAt: Date;
  updatedAt: Date;
  pipelineId: string;
}

export interface SampleInformaticsResultDetailsEntry {
  informaticsPipelineResult: InformaticsPipelineResult;
  computedValues: InformaticsComputedValues;
  informaticsPipelineRun: InformaticsPipelineRun;
}

export interface SequencingFastQ {
  file: SupplementalFile;
  type: { name: 'r1' | 'r2' };
}

export async function GetHierarchyForSampleId(
  sampleId: string,
  accessToken: string,
  decrypt: boolean = false
): Promise<ReadonlyArray<SampleHierarchy>> {
  return await BaseGet(
    `${appSettings.api.endpoint}/api/v2/SampleData/Hierarchy/${sampleId}/?decrypt=${decrypt}`,
    accessToken
  );
}

export async function GetHierarchyForSampleIds(
  sampleIds: ReadonlyArray<string>,
  accessToken: string,
  decrypt: boolean = false
): Promise<ReadonlyArray<SampleHierarchy>> {
  return await BasePost(
    `${appSettings.api.endpoint}/api/v2/SampleData/Hierarchy?decrypt=${decrypt}`,
    sampleIds,
    accessToken
  );
}

export async function GetSampleTransitionInformation(
  sampleIds: ReadonlyArray<string>,
  decrypt: boolean = false,
  accessToken?: string
): Promise<ReadonlyArray<SampleTransitionInformation>> {
  return await BasePost(
    `${appSettings.api.endpoint}/api/v2/SampleData/SampleTransformation/SampleTransitionInformation?decryptForeignHashes=${decrypt}`,
    sampleIds,
    accessToken
  );
}
export async function GetAllTransitionAndTransitionSamples(
  sampleIds: ReadonlyArray<string>,
  accessToken?: string
): Promise<ReadonlyArray<TransitionSampleDetailsDto>> {
  return await BasePost(`${appSettings.api.endpoint}/api/v2/SampleData/TransitionSamples`, sampleIds, accessToken);
}

export async function GetSampleSequencingDetails(
  sampleIds: ReadonlyArray<string>,
  accessToken?: string
): Promise<ReadonlyArray<SampleSequencingDetails>> {
  return await BasePost(`${appSettings.api.endpoint}/api/v2/SampleData/SequencingDetails`, sampleIds, accessToken);
}

export async function GetInformaticsResultDetails(
  sampleIds: ReadonlyArray<string>,
  accessToken?: string
): Promise<ReadonlyArray<SampleInformaticsResultDetails>> {
  return await BasePost(
    `${appSettings.api.endpoint}/api/v2/SampleData/InformaticsResultDetails`,
    sampleIds,
    accessToken
  );
}

export async function GetInformaticsResultDetailsResearchProjectId(
  researchProjectId: string,
  accessToken?: string
): Promise<ReadonlyArray<SampleInformaticsResultDetails>> {
  return await BaseGet(
    `${appSettings.api.endpoint}/api/v2/SampleData/InformaticsResultDetails/ResearchProjectId/${researchProjectId}`,
    accessToken
  );
}

export function ConvertToSampleHierarchyTree(hierarchy: SampleHierarchy): SampleHierarchyTree {
  const { root, hierarchy: transformationDescendants } = hierarchy;

  // Create a map of each sample to its descendants and depth
  const descendantsMap = new Map<string, Array<{ descendant: Sample; depth: number }>>();
  for (const { ancestor, descendant, depth } of transformationDescendants) {
    const ancestorId = ancestor.sampleId;
    if (!descendantsMap.has(ancestorId)) {
      descendantsMap.set(ancestorId, []);
    }
    descendantsMap.get(ancestorId)?.push({ descendant, depth });
  }

  // Recursively build the SampleHierarchyTree
  function buildTree(sample: Sample, path: string[], depth: number): SampleHierarchyTree {
    const sampleId = sample.sampleId;
    const descendants = descendantsMap.get(sampleId) ?? [];
    return {
      ancestor: sample,
      descendant: descendants.map(({ descendant }) => buildTree(descendant, [...path, sample.sampleId], depth + 1)),
      depth,
      path: [...path, sample.sampleId],
      isLeaf: descendants.length === 0,
    };
  }

  return buildTree(root, [], 0);
}

export function GetSampleSpecificBranch(
  tree: SampleHierarchyTree,
  sampleIds: ReadonlyArray<string>
): SampleHierarchyTree | undefined {
  if (sampleIds.includes(tree.ancestor.sampleId)) {
    return tree;
  }

  const matchingDescendants = tree.descendant
    .map(subTree => GetSampleSpecificBranch(subTree, sampleIds))
    .filter((node): node is SampleHierarchyTree => node !== undefined);

  if (matchingDescendants.length > 0) {
    return {
      ancestor: tree.ancestor,
      descendant: matchingDescendants,
      depth: tree.depth,
      path: tree.path,
      isLeaf: tree.isLeaf,
    };
  }

  return undefined;
}

export function GetAllSampleIds(trees: SampleHierarchyTree[]): ReadonlyArray<string> {
  function getIds(tree: SampleHierarchyTree) {
    const nodes = [tree.ancestor.sampleId];

    for (const child of tree.descendant) {
      nodes.push(...getIds(child));
    }

    return nodes;
  }

  return uniq(trees.flatMap(tree => getIds(tree)));
}

export function GetAllLeafSampleIds(trees: SampleHierarchyTree[]): ReadonlyArray<string> {
  function getIds(tree: SampleHierarchyTree) {
    const nodes = tree?.isLeaf ? [tree.ancestor.sampleId] : [];

    for (const child of tree.descendant) {
      nodes.push(...getIds(child));
    }

    return nodes;
  }

  return uniq(trees.flatMap(tree => getIds(tree)));
}

export async function CheckPatientSamplesAlreadyProcessed(
  sampleIds: ReadonlyArray<string>,
  accessToken: string
): Promise<{ [key: string]: ReadonlyArray<SampleTrackingSampleInformation> }> {
  return await BasePost(
    `${appSettings.api.endpoint}/api/${API_VERSION.v2}/SampleData/CheckPatientSamplesAlreadyProcessed`,
    sampleIds,
    accessToken
  );
}
